import { FetchResponse, FetchSuccess } from '../types';
import { EnumReportTypes } from '../types/reports';
import { EnumClosingCostType } from '../types/settings';
import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchUpdate,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from './fetch';
import { IReport, IReportsGetParams } from './reports';

export interface IUserCompany {
  name?: string;
  website?: string;
  nmls?: string;
  address?: string;
}

interface IChangeDefaultReportType {
  reportType: EnumReportTypes;
}

export interface IUserUpdateParams {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  company?: IUserCompany;
}

export interface IProfile {
  address: string | null;
  avatar: string | null;
  balance: number;
  companyName: string | null;
  customerId: string | null;
  email: string;
  firstName: string | null;
  id: number;
  isPremium: boolean;
  lastName: string | null;
  logo: string | null;
  nmls: string | null;
  nmlsId: string | null;
  companyNmlsId: string | null;
  phoneNumber: string | null;
  planId: string | null;
  subscriptionId: string | null;
  userId: number | null;
  videoUrl: string | null;
  websiteLink: string | null;
  reference: string | null;
  closingText: string | null;
  settings_logo: string | null;
  welcome_message: string | null;
  type: string | null;
  insurance?: boolean;
  permissions?: string;
  reportDefaultType?: EnumReportTypes;
  createdAt: string | null;
}

export interface IProfileUpdateParams {
  address?: string | null;
  companyName?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  nmls?: string | null;
  phoneNumber?: string | null;
  videoUrl?: string | null;
  websiteLink?: string | null;
  closingText?: string | null;
  settings_logo?: string | null;
  welcome_message?: string | null;
}

export interface ISettingItem {
  active: boolean;
  id: number;
  order: number;
  text: string;
  type: EnumClosingCostType;
  userId?: number;
  name: string;
  amount?: number;
  aprIncluded: boolean;
  propertyTaxesMonthsCollected: number;
  daysCollected: number;
}

export interface ILoanClosingCost {
  id: number;
  amount: number;
  loanId: number;
  editable: boolean;
  text: string;
  type: EnumClosingCostType;
  name: string;
  reportId: string;
  createdAt: string;
  updatedAt: string;
  propertyTaxesMonthsCollected: number;
  daysCollected: number;
}

export interface IUserSettings {
  generalNotification: ISettingItem[];
  marketNotification: ISettingItem[];
  reportView: ISettingItem[];
  tableView: ISettingItem[];
}

export interface IUserSettingsParams {
  generalNotification?: ISettingItem[];
  marketNotification?: ISettingItem[];
  reportView?: ISettingItem[];
  tableView?: ISettingItem[];
  reportType?: EnumReportTypes;
}

export interface IReferralParams {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  comment?: string;
  id: string;
}

export interface IReferral {
  id: string;
  reportId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
}

export interface IReferralItem {
  Report: IReport;
  comment: string;
  createdAt: string;
  email: string;
  fullName: string;
  id: string | number;
  phoneNumber: string;
  reportId: string | number;
  updatedAt: string;
  status: 'new' | 'contacted';
}

export interface IReferrals {
  referrals: IReferralItem[];
}

export interface IUserClosingCostSettings {
  id: number;
  userId: string;
  name: string;
  text: string;
  order: number;
  active: number;
  type: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
  aprIncluded: boolean;
  propertyTaxesMonthsCollected: number;
  daysCollected: number;
}

export interface IUserClosingCostBody {
  userId: string;
  name: string;
  text: string;
  order: number;
  active: number;
  type: string;
}

export const useUserId = (): FetchGetId<FetchResponse<IProfile>> =>
  useFetchGetId('users/user', '', { autoStart: false });

export const useLoanOfficerId = (): FetchGetId<FetchResponse<IProfile>> =>
  useFetchGetId('/loans/officier', '', { autoStart: false });

export const useUserUpdate = (): FetchUpdate<FetchResponse<IProfile>, DefaultFetchError, IUserUpdateParams> =>
  useFetchUpdate('users');

export const useProfileId = (): FetchGetId<FetchResponse<IProfile>> =>
  useFetchGetId('users/profile', '', { autoStart: false, multiple: 'profile' });

export const useProfileUpdate = (): FetchUpdate<IProfile, DefaultFetchError, IProfileUpdateParams> =>
  useFetchUpdate('users/profile');

export const useUserFileUpload = (): FetchCreate<FetchResponse<string>, DefaultFetchError, FormData> =>
  useFetchCreate('users/file-upload');

export const useUserSettings = (): FetchGet<FetchResponse<IUserSettings>, { reportType: EnumReportTypes }> =>
  useFetchGet('settings', { autoStart: false });

export const useUserClosingCostSettings = (): FetchGetId<FetchResponse<IUserClosingCostSettings>> =>
  useFetchGetId(`settings/closing-cost`, '', { autoStart: false });

export const useUserClosingCostDelete = (): FetchDelete<FetchSuccess> =>
  useFetchDelete('settings/closing-cost');

export const useUserClosingCostCreate = ():
  FetchCreate<FetchResponse<IUserClosingCostSettings>, DefaultFetchError, IUserClosingCostBody> =>
  useFetchCreate('settings/closing-cost', {
    autoStart: false,
  });

export const useUserClosingCostUpdate = ():
  FetchUpdate<FetchResponse<string>, DefaultFetchError, IUserClosingCostBody> =>
  useFetchUpdate('settings/closing-cost');

export const useUserClosingCostOrder = ():
  FetchUpdate<FetchResponse<string>, DefaultFetchError, { closingCosts:  { order: number; id: number }[] }> =>
    useFetchUpdate('settings/closing-cost/order') ;

export const useUserSettingsUpdate = (): FetchUpdate<FetchResponse<string>, DefaultFetchError, IUserSettingsParams> =>
  useFetchUpdate('settings');

export function useReferrals<D = FetchResponse<IReferrals>, DD = D>(
  decorateData?: (data: D) => DD
): FetchGet<DD, IReportsGetParams> {
  return useFetchGet<D, DefaultFetchError, unknown, DD>('users/referral', { decorateData, autoStart: false });
}

export const useCreateReferral = (): FetchCreate<FetchResponse<IReferral>, DefaultFetchError, IReferralParams> =>
  useFetchCreate('users/referral');

export const useUserDelete = (): FetchDelete<FetchSuccess> => useFetchDelete('users');

export const useChangeDefaultReportType = ():
  FetchUpdate<FetchResponse<string>,
    DefaultFetchError, IChangeDefaultReportType> =>
  useFetchUpdate('users/default-report-type');
